import { Alert, Box, Divider, Link, Stack, TextField, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/service/dataprovider';
import palette from '../../shared/theme/palette';
import tokens from '../../shared/theme/tokens';
import Panel from '../../components/Panel';
import StatusChip from '../../components/StatusChip';
import { formatPhoneNumber, getPersonFullName } from '../../shared/util';
import moment from 'moment';
import PaymentInfo from './PaymentInfo';
import ResmanLedger from './ResmanLedger';
import LeaseRequests from './LeaseRequests';
import { useEffect, useMemo, useState } from 'react';
import { IOSSwitch } from '../../components/switch';
import toast from 'react-hot-toast';
import ResetPasswordConfirmModal from './ResetPasswordConfirmModal';
import ResetPasswordModal from './ResetPasswordModal';
import CredBuildConfirmModal from './CredBuildConfirmModal';
import { useTranslation } from 'react-i18next';
import { CustomTooltip } from '../../components/tooltip';
import IdvFailurePanel from './IdvFailurePanel';
import EmailAssociationsPanel from './EmailAssociationsPanel';
import RebindPersonConfirmModal from './RebindPersonConfirmModal';

export default function LeaseResident({ lease, leaseLink }) {
  const personId = leaseLink.people?.id;
  const personQueryKey = useMemo(() => ['person', personId, lease.id], [personId, lease.id]);
  const { data, error } = useQuery({
    queryKey: personQueryKey,
    queryFn: () => api.get(`/v1/admin/person/${personId}`, { params: { leaseId: lease.id } }),
    enabled: Boolean(personId),
  });
  const person = data?.data;

  if (error) {
    return (
      <Typography variant="h6" sx={{ color: palette.error[50] }}>
        {error?.response?.data?.message ?? `There is an error for loading person ${leaseLink.id}. Try again later`}
      </Typography>
    );
  }
  if (!person) return null;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', alignItems: 'start', gap: 3 }}>
      <ResidentDetail person={person} lease={lease} />
      <PaymentInfo person={person} lease={lease} />
      <Stack gap={3}>
        <ResmanLedger lease={lease} person={person} />
        <LeaseRequests lease={lease} />
      </Stack>
    </Box>
  );
}

function ResidentDetail({ person, lease }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: userResponse } = useQuery({
    queryKey: ['user'],
    queryFn: () => api.get('/v1/admin/profile'),
  });
  const userProfile = userResponse?.data;
  const personQueryKey = useMemo(() => ['person', person.id, lease.id], [person.id, lease.id]);
  const currentEmail = person.relatedUser?.email ?? person.Email ?? '';

  const [isEditingEmail, setEditingEmail] = useState(false);
  const [isEditingPhone, setEditingPhone] = useState(false);
  const [email, setEmail] = useState(currentEmail);
  const [phoneNumber, setPhoneNumber] = useState(person.HomePhone || person.WorkPhone || person.MobilePhone);
  const [showResetPasswordConfirmModal, setShowResetPasswordConfirmModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showCredBuildConfirmModal, setShowCredBuildConfirmModal] = useState(false);
  const [throttleSendInvite, setThrottleSendInvite] = useState(false);
  // Invite link returned by POST /invite-user, surfaced so staff can copy and send it
  // manually when the email is not delivered.
  const [inviteUrl, setInviteUrl] = useState(null);
  const loginOnOtherLease = person.loginOnOtherLease ?? null;
  const [showEmailAssociations, setShowEmailAssociations] = useState(Boolean(loginOnOtherLease));
  const [rebindUser, setRebindUser] = useState(null);

  useEffect(() => {
    setEmail(currentEmail);
    setPhoneNumber(person.HomePhone || person.WorkPhone || person.MobilePhone);
  }, [currentEmail, person]);

  const normalizedEmail = email.trim().toLowerCase();
  const isValidEmail = useMemo(() => {
    if (!normalizedEmail) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
  }, [normalizedEmail]);

  const contactErrorMessage = data =>
    data?.response?.data?.err ?? data?.response?.data?.message ?? data?.response?.message ?? data?.message ?? 'Failed to update contact information';

  const refreshPerson = () => {
    queryClient.invalidateQueries({ queryKey: personQueryKey });
    queryClient.invalidateQueries({ queryKey: [`leaseDetail/${lease.id}`] });
  };

  const changeContactMutation = useMutation({
    mutationFn: contact => api.post('/v1/admin/change-contact', contact),
    onError: data => {
      toast.error(contactErrorMessage(data));
    },
  });
  const canSaveEmail = isValidEmail && !changeContactMutation.isPending;
  const toggleCredBuildMutation = useMutation({
    mutationFn: payload => api.post(`/v1/admin/user/${person.relatedUser.id}/toggle-credbuild-requirement`, payload),
  });
  const resetPasswordEmailMutation = useMutation({
    mutationFn: payload => api.post(`/v1/admin/reset-password-email`, payload),
  });
  const loginAsUserMutation = useMutation({
    mutationFn: () => api.post(`/v1/admin/lease/${lease.id}/login-as-user`, { userId: person.relatedUser.id }),
    onSuccess: res => {
      const portalUrl = res?.data?.portalUrl;
      if (portalUrl) window.open(portalUrl, '_blank', 'noopener');
      toast.success(t('lease.resident.loginAsSuccess'));
    },
    onError: e => toast.error(e?.response?.data?.message ?? t('lease.resident.loginAsFailed')),
  });
  const rebindPersonMutation = useMutation({
    mutationFn: ({ userId, reason }) =>
      api.post(`/v1/admin/lease/${lease.id}/rebind-person`, {
        userId,
        targetPersonId: person.id,
        confirm: true,
        reason,
      }),
    onSuccess: () => {
      toast.success(t('lease.resident.rebindSuccess'));
      setRebindUser(null);
      setShowEmailAssociations(false);
      refreshPerson();
    },
    onError: e => toast.error(e?.response?.data?.message ?? e?.response?.data?.err ?? t('lease.resident.rebindFailed')),
  });
  const toggleSSNRequirementMutation = useMutation({
    mutationFn: payload => api.post(`/v1/admin/person/${person.id}/toggle-ssn-requirement`, payload),
  });
  const resendInvitationMutation = useMutation({
    mutationFn: contact => api.post('/v1/admin/invite-user', contact),
    onSuccess: res => {
      setInviteUrl(res?.data?.inviteUrl ?? null);
      toast.success(t('lease.success.invite', { property: lease.property.name }));
      refreshPerson();
    },
    onError: data => {
      const msg = data?.response?.data?.err ?? data?.response?.data?.message ?? data?.response?.message ?? 'Failed to send invitation';
      toast.error(msg);
      if (userProfile?.isSuperAdmin && typeof msg === 'string' && msg.includes('User already exists for email')) {
        setShowEmailAssociations(true);
      }
    },
  });

  const resendApplicationInvitationMutation = useMutation({
    mutationFn: contact => api.post('/v1/admin/invite-application-user', contact),
    onSuccess: () => {
      toast.success(t('lease.success.invite', { property: person.fullName }));
      refreshPerson();
    },
    onError: data => {
      toast.error(data?.response?.data?.err ?? data?.response?.data?.message ?? data?.response?.message ?? 'Failed to send invitation');
    },
  });

  const refreshLeaseResmanMutation = useMutation({
    mutationFn: payload => api.post(`/v1/admin/refresh-lease-resman`, payload),
  });

  const lastInvitation = useMemo(() => {
    const invitations = person.invitations ?? [];
    invitations.sort((i1, i2) => new Date(i2.updatedDate).getTime() - new Date(i1.updatedDate).getTime());

    return invitations[0];
  }, [person]);

  const handleSaveEmail = () => {
    if (changeContactMutation.isPending) return;
    if (!isValidEmail) {
      toast.error(t('lease.applicant.invalidEmail'));
      return;
    }
    changeContactMutation.mutate(
      {
        personId: person.id,
        email: normalizedEmail,
      },
      {
        onSuccess: () => {
          toast.success(`Email address for ${lease.property.name} has been changed.`);
          setEmail(normalizedEmail);
          setEditingEmail(false);
          refreshPerson();
        },
      }
    );
  };

  const handleSavePhone = () => {
    if (changeContactMutation.isPending) return;
    changeContactMutation.mutate(
      {
        personId: person.id,
        phone: phoneNumber,
      },
      {
        onSuccess: () => {
          toast.success(`Phone number for ${lease.property.name} has been changed.`);
          setEditingPhone(false);
          refreshPerson();
        },
      }
    );
  };

  const handleResendInvitation = () => {
    resendInvitationMutation.mutate({
      propertyId: lease.property.id,
      personId: person.id,
      leaseId: lease.id,
    });
  };

  const handleCopyInviteLink = () => {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl).then(
      () => toast.success(t('lease.resident.linkCopied')),
      () => toast.error(t('lease.resident.linkCopyFailed'))
    );
  };

  const handleCopyResumeLink = () => {
    const resumeUrl = person.application?.resumeUrl;
    if (!resumeUrl) return;
    navigator.clipboard.writeText(resumeUrl).then(
      () => toast.success(t('lease.resident.resumeLinkCopied')),
      () => toast.error(t('lease.resident.resumeLinkCopyFailed'))
    );
  };

  const handleCredBuildChange = () => {
    if (person.relatedUser) {
      toggleCredBuildMutation.mutate(
        { value: person.relatedUser.isCreditReportingEnabled !== null && person.relatedUser.isCreditReportingEnabled !== true ? true : false },
        {
          onSuccess: () => {
            refreshPerson();
          },
        }
      );
    }
  };

  const handleToggleSSNRequirement = e => {
    toggleSSNRequirementMutation.mutate(
      { value: e.target.checked },
      {
        onSuccess: () => {
          toast.success(`SSN requirement was removed for ${lease.property.name}.`);
          refreshPerson();
        },
      }
    );
  };

  const handleResetPasswordEmail = () => {
    if (person.relatedUser) {
      resetPasswordEmailMutation.mutate(
        {
          userId: person.relatedUser.id,
        },
        {
          onSuccess: () => {
            toast.success(`Send reset password email for ${person.relatedUser.email}.`);
            refreshPerson();
          },
        }
      );
    }
  };

  const handelSyncWithResman = () => {
    refreshLeaseResmanMutation.mutate(
      {
        propertyId: lease.property.id,
        personId: person.id,
        unitNumber: lease.Unit,
        leaseId: lease.id,
      },
      {
        onSuccess: () => {
          toast.success(`Sync with resman for ${lease.property.name} successfully.`);
          refreshPerson();
        },
      }
    );
  };

  return (
    <Panel padding={3}>
      <Stack direction="row" gap={1} justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" gap={1} alignItems="center">
          <Typography sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: palette.grey[50] }}>{t('lease.resident.title')}</Typography>
          {!person.prospectId && (
            <CustomTooltip title={t('lease.resident.residentInfo')}>
              <InfoOutlinedIcon color="warning" sx={editIconSx} />
            </CustomTooltip>
          )}
          {person.prospectId && person.relatedUser && person.Email !== person.relatedUser?.email && (
            <CustomTooltip title={t('lease.resident.outsync')}>
              <InfoOutlinedIcon color="warning" sx={editIconSx} />
            </CustomTooltip>
          )}
        </Stack>
        {!person.prospectId && (
          <Link component="button" type="button" onClick={handelSyncWithResman} sx={{ fontWeight: 500 }}>
            {t('lease.resident.sync')}
          </Link>
        )}
      </Stack>
      <Stack gap={2}>
        <Typography>
          <Box component="span" sx={{ color: palette.grey[50] }}>
            {t('lease.resident.name')}{' '}
          </Box>
          <Box component="span" sx={{ color: palette.grey[80] }}>
            {getPersonFullName(person)}
          </Box>
        </Typography>
        {isEditingEmail ? (
          <Stack gap={2} sx={{ my: 1 }}>
            <Box>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                error={Boolean(email.trim()) && !isValidEmail}
                helperText={Boolean(email.trim()) && !isValidEmail ? t('lease.applicant.invalidEmail') : ''}
              />
              <Typography variant="overline" color="grey.400">
                {t('lease.resident.editingEmail')}
              </Typography>
            </Box>
            <Stack gap={1.5} flexDirection="row" justifyContent="flex-end">
              <Typography
                onClick={() => {
                  setEmail(currentEmail);
                  setEditingEmail(false);
                }}
                color="grey.500"
                sx={{ fontWeight: 500, borderBottom: `1px solid ${tokens.colors.primaryTint}`, cursor: 'pointer' }}
              >
                {t('common.cancel')}
              </Typography>
              <Typography
                onClick={handleSaveEmail}
                aria-disabled={!canSaveEmail}
                color={canSaveEmail ? 'primary' : 'grey.400'}
                sx={{ fontWeight: 500, borderBottom: `1px solid ${tokens.colors.primaryTint}`, cursor: canSaveEmail ? 'pointer' : 'not-allowed' }}
              >
                {t('common.save')}
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography>
              <Box component="span" sx={{ color: palette.grey[50] }}>
                {t('lease.resident.email')}{' '}
              </Box>
              <Box component="span" sx={{ color: palette.grey[80] }}>
                {person.relatedUser?.email ?? person.Email}
              </Box>
            </Typography>
            {person.prospectId && <EditOutlinedIcon onClick={() => setEditingEmail(true)} color="primary" sx={editIconSx} />}
          </Stack>
        )}
        {isEditingPhone ? (
          <Stack gap={2} sx={{ my: 1 }}>
            <TextField fullWidth label="Phone Number" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} />
            <Stack gap={1.5} flexDirection="row" justifyContent="flex-end">
              <Typography
                onClick={() => {
                  setPhoneNumber(person.HomePhone || person.WorkPhone || person.MobilePhone);
                  setEditingPhone(false);
                }}
                color="grey.500"
                sx={{ fontWeight: 500, borderBottom: `1px solid ${tokens.colors.primaryTint}`, cursor: 'pointer' }}
              >
                {t('common.cancel')}
              </Typography>
              <Typography
                onClick={handleSavePhone}
                color="primary"
                sx={{ fontWeight: 500, borderBottom: `1px solid ${tokens.colors.primaryTint}`, cursor: 'pointer' }}
              >
                {t('common.save')}
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography>
              <Box component="span" sx={{ color: palette.grey[50] }}>
                {t('lease.resident.phone')}{' '}
              </Box>
              <Box component="span" sx={{ color: palette.grey[80] }}>
                {formatPhoneNumber(person.HomePhone || person.WorkPhone || person.MobilePhone)}
              </Box>
            </Typography>
            {person.prospectId && <EditOutlinedIcon onClick={() => setEditingPhone(true)} color="primary" sx={editIconSx} />}
          </Stack>
        )}
        <Typography>
          <Box component="span" sx={{ color: palette.grey[50] }}>
            {t('lease.resident.leasingFrom')}{' '}
          </Box>
          <Box component="span" sx={{ color: palette.grey[80] }}>
            {`${moment(lease.LeaseStartDate).utc().format('MM/DD/YYYY')} - ${moment(lease.LeaseEndDate).utc().format('MM/DD/YYYY')}`}
          </Box>
        </Typography>
        {person.birthDate && (
          <Typography>
            <Box component="span" sx={{ color: palette.grey[50] }}>
              {t('lease.resident.birthDate')}{' '}
            </Box>
            <Box component="span" sx={{ color: palette.grey[80] }}>
              {moment(person.birthDate).utc().format('MM/DD/YYYY')}
            </Box>
          </Typography>
        )}
        {userProfile?.isSuperAdmin && (
          <Typography>
            <Box component="span" sx={{ color: palette.grey[50] }}>
              {t('lease.resident.status')}{' '}
            </Box>
            <Box component="span" sx={{ color: palette.grey[80] }}>
              {lease.Status}
            </Box>
          </Typography>
        )}
        {userProfile?.isSuperAdmin && (
          <Typography>
            <Box component="span" sx={{ color: palette.grey[50] }}>
              {t('lease.resident.moveOutDate')}{' '}
            </Box>
            <Box component="span" sx={{ color: palette.grey[80] }}>
              {moment(lease.MoveOutDate).utc().format('MM/DD/YYYY')}
            </Box>
          </Typography>
        )}
        {/* <Typography>
          <Box component="span" sx={{ color: palette.grey[50] }}>
            Property:{' '}
          </Box>
          <Box component="span" sx={{ color: palette.grey[80] }}>
            {lease.property.name}
          </Box>
        </Typography>
        <Typography>
          <Box component="span" sx={{ color: palette.grey[50] }}>
            Unit Number:{' '}
          </Box>
          <Box component="span" sx={{ color: palette.grey[80] }}>
            {lease.Unit}
          </Box>
        </Typography> */}
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Stack gap={2}>
        {person.application && (
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography>
                <Box component="span" sx={{ color: palette.grey[50] }}>
                  {t('lease.resident.applicationStatus')}
                </Box>
              </Typography>
              {person.birthDate && moment().diff(moment(person.birthDate), 'years') < 18 ? (
                <StatusChip tone="neutral" label="N/A for Minors" />
              ) : (
                <StatusChip
                  tone={applicationStatusTone[person.application.status] ?? 'info'}
                  label={person.application.status}
                  sx={{ textTransform: 'capitalize' }}
                />
              )}
            </Stack>
            {person.relatedUser && person.application.resumeUrl && !(person.birthDate && moment().diff(moment(person.birthDate), 'years') < 18) ? (
              <>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography>
                    <Box component="span" sx={{ color: palette.grey[50] }}>
                      {t('lease.resident.resumeLink')}
                    </Box>
                  </Typography>
                  <Link component="button" type="button" onClick={handleCopyResumeLink}>
                    {t('lease.resident.copyResumeLink')}
                  </Link>
                </Stack>
                <Alert severity="info" sx={{ fontWeight: 500 }}>
                  {t('lease.resident.resumeLinkHint')}
                </Alert>
              </>
            ) : null}
          </>
        )}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography>
            <Box component="span" sx={{ color: palette.grey[50] }}>
              {t('lease.resident.registered')}
            </Box>
          </Typography>
          <StatusChip
            tone={person.relatedUser && !loginOnOtherLease ? 'success' : 'info'}
            label={t(`common.${person.relatedUser && !loginOnOtherLease ? 'success' : 'pending'}`)}
          />
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography>
            <Box component="span" sx={{ color: palette.grey[50] }}>
              {t('lease.resident.lastLogin')}
            </Box>
          </Typography>
          {person.relatedUser?.lastLogin && !loginOnOtherLease && (
            <Typography>
              <Box component="span" sx={{ color: palette.grey[80] }}>
                {moment(person.relatedUser?.lastLogin).format('MM/DD/YYYY')}
                {' at '}
                {moment(person.relatedUser?.lastLogin).format('h:mm A')}
              </Box>
            </Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography>
            <Box component="span" sx={{ color: palette.grey[50] }}>
              {t('lease.resident.ssnVerified')}
            </Box>
          </Typography>
          <StatusChip
            tone={person.identityVerificationSessions?.length > 0 ? kycStatusTone[person.kycStatus] : 'info'}
            label={person.identityVerificationSessions?.length > 0 ? t(`common.${kycStatusLabel[person.kycStatus]}`) : 'Not Done'}
          />
        </Stack>
        {person.kycStatus === 'Failed' &&
          (() => {
            // Latest session by createdAt — that's the one whose failure_details snapshot is current.
            const latest = [...(person.identityVerificationSessions || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
            if (!latest?.sessionId) return null;
            return <IdvFailurePanel sessionId={latest.sessionId} sessionType={latest.type} />;
          })()}
        {userProfile?.isSuperAdmin && person.relatedUser && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography>
              <Box component="span" sx={{ color: palette.grey[50] }}>
                {t('lease.resident.credbuild')}
              </Box>
            </Typography>
            <IOSSwitch
              checked={person.relatedUser.isCreditReportingEnabled !== null && person.relatedUser.isCreditReportingEnabled !== true}
              onChange={() => setShowCredBuildConfirmModal(true)}
            />
          </Stack>
        )}
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Stack gap={2}>
        {person.kycStatus !== 'Success' && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography>
              <Box component="span" sx={{ color: palette.grey[50] }}>
                {t('lease.resident.removeSSN')}
              </Box>
            </Typography>
            <IOSSwitch checked={person.refusedKyc} onChange={handleToggleSSNRequirement} />
          </Stack>
        )}
        {person.relatedUser && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography>
              <Box component="span" sx={{ color: palette.grey[50] }}>
                {t('lease.resident.password')}{' '}
              </Box>
              <Box component="span" sx={{ color: palette.grey[80] }}>
                {'************'}
              </Box>
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Link component="button" type="button" onClick={setShowResetPasswordConfirmModal}>
                {t('common.reset')}
              </Link>
              <Divider variant="horizontal" />
              <Link component="button" type="button" onClick={() => setShowResetPasswordModal(true)}>
                {t('common.change')}
              </Link>
            </Stack>
          </Stack>
        )}
        {person.relatedUser && !loginOnOtherLease && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ color: palette.grey[50] }}>{t('lease.resident.loginAsUser')}</Typography>
            <Link component="button" type="button" disabled={loginAsUserMutation.isPending} onClick={() => loginAsUserMutation.mutate()}>
              {loginAsUserMutation.isPending ? t('lease.resident.loginAsOpening') : t('lease.resident.loginAsUser')}
            </Link>
          </Stack>
        )}
        {loginOnOtherLease && (
          <>
            <Alert severity="warning">
              {t('lease.resident.loginOnOtherLease', {
                name: loginOnOtherLease.personName || loginOnOtherLease.email || t('lease.resident.rebindUnknownPerson'),
              })}
            </Alert>
            {userProfile?.isSuperAdmin && (person.Email || email) && (
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ color: palette.grey[50] }}>{t('lease.resident.accountsForEmail')}</Typography>
                <Link component="button" type="button" onClick={() => setShowEmailAssociations(v => !v)}>
                  {showEmailAssociations ? t('lease.resident.hideAccounts') : t('lease.resident.showAccounts')}
                </Link>
              </Stack>
            )}
          </>
        )}

        {!person.relatedUser && (
          <>
            {userProfile?.isSuperAdmin && (person.Email || email) && (
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ color: palette.grey[50] }}>{t('lease.resident.accountsForEmail')}</Typography>
                <Link component="button" type="button" onClick={() => setShowEmailAssociations(v => !v)}>
                  {showEmailAssociations ? t('lease.resident.hideAccounts') : t('lease.resident.showAccounts')}
                </Link>
              </Stack>
            )}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography>
                <Box component="span" sx={{ color: palette.grey[50] }}>
                  {t('lease.resident.inviteSent')}{' '}
                </Box>
                <Box component="span" sx={{ color: palette.grey[80] }}>
                  {lastInvitation?.updatedDate ? moment(lastInvitation?.updatedDate).format('MM/DD/YY h:mm a') : ''}
                </Box>
              </Typography>
              {throttleSendInvite ? (
                <Typography sx={{ color: tokens.colors.inkFaint, fontWeight: 500 }}>
                  {person.invitations?.length > 1 ? 'Resent' : 'Sent'}(1 minute before resend)
                </Typography>
              ) : (
                <Link component="button" type="button" onClick={handleResendInvitation}>
                  {lastInvitation?.updatedDate ? t('lease.resident.resendInvitation') : t('lease.resident.sendInvitation')}
                </Link>
              )}
            </Stack>
            {inviteUrl && (
              <>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box component="span" sx={{ color: palette.grey[50] }}>
                    {t('lease.resident.inviteLink')}
                  </Box>
                  <Link component="button" type="button" onClick={handleCopyInviteLink}>
                    {t('lease.resident.copyInviteLink')}
                  </Link>
                </Stack>
                <Alert severity="warning" sx={{ mt: 0.5, fontWeight: 500 }}>
                  {t('lease.resident.inviteLinkEmailHint')}
                </Alert>
              </>
            )}
          </>
        )}
      </Stack>
      {userProfile?.isSuperAdmin && showEmailAssociations && (person.Email || email) && (
        <EmailAssociationsPanel
          email={person.Email || email}
          currentPersonId={person.id}
          connecting={rebindPersonMutation.isPending}
          onConnectUser={user => setRebindUser(user)}
        />
      )}
      {userProfile?.isSuperAdmin && rebindUser && (
        <RebindPersonConfirmModal
          onClose={() => setRebindUser(null)}
          loading={rebindPersonMutation.isPending}
          userEmail={rebindUser.email}
          sourcePersonName={rebindUser.person ? `${rebindUser.person.firstName} ${rebindUser.person.lastName}` : null}
          targetPersonName={getPersonFullName(person)}
          activePaymentMethodCount={rebindUser.activePaymentMethodCount ?? 0}
          autopayActive={!!rebindUser.autopayActive}
          hasOpenCredBuild={(rebindUser.openCreditAccountLeaseIds ?? []).length > 0}
          requireMoveLoginPrefix={(rebindUser.person?.leases ?? []).some(l =>
            ['Current', 'Pending Renewal', 'Under Eviction', 'Notice to Vacate', 'Month to Month'].includes(l.status)
          )}
          onSubmit={reason => rebindPersonMutation.mutate({ userId: rebindUser.id, reason })}
        />
      )}
      {showResetPasswordConfirmModal && (
        <ResetPasswordConfirmModal
          onClose={() => setShowResetPasswordConfirmModal(false)}
          onSubmit={() => {
            handleResetPasswordEmail();
            setShowResetPasswordConfirmModal(false);
          }}
        />
      )}
      {showCredBuildConfirmModal && (
        <CredBuildConfirmModal
          onClose={() => setShowCredBuildConfirmModal(false)}
          onSubmit={() => {
            handleCredBuildChange();
            setShowCredBuildConfirmModal(false);
          }}
        />
      )}
      {showResetPasswordModal && person.relatedUser && (
        <ResetPasswordModal userId={person.relatedUser.id} residentName={lease.property.name} onClose={() => setShowResetPasswordModal(false)} />
      )}
    </Panel>
  );
}

const editIconSx = {
  width: '20px',
  height: '20px',
  cursor: 'pointer',
};

const kycStatusLabel = {
  Skipped: 'skipped',
  Failed: 'failed',
  Success: 'success',
  NotRequired: 'notRequired',
};

const kycStatusTone = {
  Skipped: 'warning',
  Failed: 'error',
  Success: 'success',
  NotRequired: 'info',
  NotDone: 'info',
};

const applicationStatusTone = {
  pending: 'warning',
  submitted: 'info',
  completed: 'success',
  cancelled: 'neutral',
  denied: 'error',
};
